<template>
  <div class="design-comparison p-4">
    <!-- <h2 class="text-xl font-bold mb-6">Website Design Comparison Scanner</h2> -->

    <!-- ── Input form ────────────────────────────────────────────────────── -->
    <div class="bg-gray-50 p-6 rounded-lg border mb-6">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-1">
            3D File Link
          </label>
          <input
            v-model="threeDFileUrl"
            type="url"
            placeholder="https://example-3d.com"
            class="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400 outline-none"
            :disabled="comparing"
          />
        </div>
        <div>
          <label class="block text-sm font-semibold text-gray-700 mb-1">
            Live Link
          </label>
          <input
            v-model="liveUrl"
            type="url"
            placeholder="https://example-live.com"
            class="w-full border rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400 outline-none"
            :disabled="comparing"
          />
        </div>
      </div>

      <div class="flex items-center gap-3">
        <button
          @click="runComparison"
          :disabled="comparing || !threeDFileUrl || !liveUrl"
          class="px-5 py-2 bg-blue-600 text-white rounded font-medium text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          <span v-if="comparing" class="inline-flex items-center gap-2">
            <span
              class="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4"
            ></span>
            Comparing...
          </span>
          <span v-else class="inline-flex items-center gap-1.5">
            <Search class="w-4 h-4" />
            <span>Compare Designs</span>
          </span>
        </button>

        <button
          v-if="report && !comparing"
          @click="resetForm"
          class="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 underline"
        >
          Clear & Start New
        </button>
      </div>

      <p v-if="error" class="mt-3 text-sm text-red-600 bg-red-50 p-2 rounded">
        {{ error }}
      </p>
    </div>

    <!-- ── Results ───────────────────────────────────────────────────────── -->
    <div v-if="report" class="space-y-6">
      <!-- Overall Score Card -->
      <div
        class="p-6 rounded-lg border-2 text-center"
        :class="
          report.status === 'PASS'
            ? 'border-green-400 bg-green-50'
            : 'border-red-400 bg-red-50'
        "
      >
        <div
          class="text-5xl font-extrabold mb-2"
          :class="report.status === 'PASS' ? 'text-green-700' : 'text-red-700'"
        >
          {{ report.similarityScore }}%
        </div>
        <div
          class="text-lg font-semibold flex items-center justify-center gap-1.5"
          :class="report.status === 'PASS' ? 'text-green-700' : 'text-red-700'"
        >
          <CheckCircle2
            v-if="report.status === 'PASS'"
            class="w-5 h-5 text-green-600"
          />
          <XCircle v-else class="w-5 h-5 text-red-600" />
          <span>{{ report.status === "PASS" ? "PASS" : "FAIL" }}</span>
          <span class="text-sm font-normal text-gray-500"
            >(threshold: 95%)</span
          >
        </div>
        <p class="text-sm text-gray-600 mt-1">{{ report.summary }}</p>
        <p class="text-xs text-gray-400 mt-1">{{ report.timestamp }}</p>
      </div>

      <!-- Pages Scanned — per-page breakdown -->
      <div
        v-if="report.pages && report.pages.length"
        class="border rounded-lg bg-white p-4"
      >
        <h3 class="text-base font-bold mb-3 flex items-center gap-2">
          <FileText class="w-4 h-4 text-blue-600" />
          <span>Pages Scanned</span>
          <span class="text-sm font-normal text-gray-400"
            >({{ report.pagesCompared }} total)</span
          >
        </h3>
        <div class="space-y-2">
          <div
            v-for="(page, i) in report.pages"
            :key="'page-' + i"
            class="border rounded-lg overflow-hidden"
          >
            <!-- Page header row -->
            <div
              class="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 transition"
              :class="page.isHomepage ? 'bg-blue-50' : ''"
              @click="togglePageDetail(i)"
            >
              <div class="flex items-center gap-3 min-w-0">
                <span class="flex-shrink-0">
                  <Home v-if="page.isHomepage" class="w-5 h-5 text-blue-600" />
                  <FileText v-else class="w-5 h-5 text-gray-500" />
                </span>
                <div class="min-w-0">
                  <span
                    class="font-mono text-sm font-semibold text-gray-800 truncate block"
                    >{{ page.path }}</span
                  >
                  <span
                    v-if="page.isHomepage"
                    class="text-xxs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold"
                    >FULL SCAN</span
                  >
                  <span
                    v-else
                    class="text-xxs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded"
                    >quick scan</span
                  >
                </div>
              </div>
              <div class="flex items-center gap-3 flex-shrink-0">
                <!-- Mini score bar -->
                <div class="hidden sm:flex items-center gap-2">
                  <div
                    class="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden"
                  >
                    <div
                      class="h-full rounded-full transition-all"
                      :class="
                        page.similarityScore >= 95
                          ? 'bg-green-500'
                          : page.similarityScore >= 80
                          ? 'bg-yellow-500'
                          : 'bg-red-500'
                      "
                      :style="{ width: page.similarityScore + '%' }"
                    ></div>
                  </div>
                </div>
                <span
                  class="text-sm font-bold"
                  :class="
                    page.status === 'PASS' ? 'text-green-700' : 'text-red-600'
                  "
                  >{{ page.similarityScore }}%</span
                >
                <span
                  class="px-2 py-0.5 rounded text-xxs font-bold uppercase"
                  :class="
                    page.status === 'PASS'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  "
                  >{{ page.status }}</span
                >
                <ChevronUp
                  v-if="expandedPages[i]"
                  class="w-4 h-4 text-gray-400"
                />
                <ChevronDown v-else class="w-4 h-4 text-gray-400" />
              </div>
            </div>

            <!-- Expanded detail panel -->
            <div
              v-if="expandedPages[i]"
              class="px-4 py-3 bg-gray-50 border-t space-y-3"
            >
              <!-- Screenshots row -->
              <div v-if="page.threeDFileUrl || page.screenshots">
                <p
                  class="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1.5"
                >
                  <Camera class="w-3.5 h-3.5 text-purple-600" />
                  <span>Screenshots</span>
                </p>
                <!-- Homepage: 3-viewport grid -->
                <div
                  v-if="page.screenshots"
                  class="grid grid-cols-1 md:grid-cols-3 gap-3"
                >
                  <div
                    v-for="ss in page.screenshots"
                    :key="ss.viewport"
                    class="text-center"
                  >
                    <p class="text-xxs text-gray-400 mb-1">
                      {{ ss.viewport }}
                    </p>
                    <div class="grid grid-cols-3 gap-1">
                      <img
                        v-if="ss.threeDFileUrl"
                        :src="ss.threeDFileUrl"
                        class="w-full h-auto border rounded cursor-pointer hover:opacity-80"
                        @click="openLightbox(ss.threeDFileUrl)"
                      />
                      <img
                        v-if="ss.liveUrl"
                        :src="ss.liveUrl"
                        class="w-full h-auto border rounded cursor-pointer hover:opacity-80"
                        @click="openLightbox(ss.liveUrl)"
                      />
                      <img
                        v-if="ss.diffUrl"
                        :src="ss.diffUrl"
                        class="w-full h-auto border rounded cursor-pointer hover:opacity-80"
                        @click="openLightbox(ss.diffUrl)"
                      />
                    </div>
                  </div>
                </div>
                <!-- Sub-page: single 3-column row -->
                <div v-else class="grid grid-cols-3 gap-2">
                  <div class="text-center">
                    <p class="text-xxs text-gray-400 mb-1">3D File</p>
                    <img
                      v-if="page.threeDFileUrl"
                      :src="page.threeDFileUrl"
                      class="w-full h-auto border rounded cursor-pointer hover:opacity-80"
                      @click="openLightbox(page.threeDFileUrl)"
                    />
                    <div
                      v-else
                      class="w-full h-16 bg-gray-200 flex items-center justify-center text-gray-400 text-xs"
                    >
                      —
                    </div>
                  </div>
                  <div class="text-center">
                    <p class="text-xxs text-gray-400 mb-1">Live</p>
                    <img
                      v-if="page.liveUrl"
                      :src="page.liveUrl"
                      class="w-full h-auto border rounded cursor-pointer hover:opacity-80"
                      @click="openLightbox(page.liveUrl)"
                    />
                    <div
                      v-else
                      class="w-full h-16 bg-gray-200 flex items-center justify-center text-gray-400 text-xs"
                    >
                      —
                    </div>
                  </div>
                  <div class="text-center">
                    <p class="text-xxs text-red-400 mb-1">Diff</p>
                    <img
                      v-if="page.diffUrl"
                      :src="page.diffUrl"
                      class="w-full h-auto border rounded cursor-pointer hover:opacity-80"
                      @click="openLightbox(page.diffUrl)"
                    />
                    <div
                      v-else
                      class="w-full h-16 bg-gray-200 flex items-center justify-center text-gray-400 text-xs"
                    >
                      —
                    </div>
                  </div>
                </div>
              </div>

              <!-- DOM diff -->
              <div v-if="page.domComparison">
                <p
                  class="text-xs font-semibold text-gray-500 mb-1 flex items-center gap-1.5"
                >
                  <Layers class="w-3.5 h-3.5 text-blue-600" />
                  <span>DOM Structure</span>
                </p>
                <p class="text-xs text-gray-600">
                  {{ page.domComparison.summary }}
                </p>
                <div
                  v-if="
                    page.domComparison.differences &&
                    page.domComparison.differences.length
                  "
                  class="mt-1 space-y-0.5"
                >
                  <div
                    v-for="(diff, j) in page.domComparison.differences"
                    :key="'pdom-' + j"
                    class="flex items-center gap-1 text-xxs border-b border-gray-100 py-0.5"
                  >
                    <code class="bg-gray-200 px-1 rounded font-mono">{{
                      diff.element
                    }}</code>
                    <span
                      :class="
                        diff.type === 'added'
                          ? 'text-green-600'
                          : 'text-red-600'
                      "
                      >{{ diff.message }}</span
                    >
                  </div>
                </div>
              </div>

              <!-- Note: full details below for homepage -->
              <p v-if="page.isHomepage" class="text-xxs text-blue-500 italic">
                ↳ Full CSS & section-by-section layout details shown below
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Screenshots & Diffs -->
      <div>
        <h3 class="text-base font-bold mb-3 flex items-center gap-2">
          <Camera class="w-4 h-4 text-purple-600" />
          <span>Screenshot Comparisons</span>
        </h3>
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div
            v-for="ss in report.screenshots"
            :key="ss.key"
            class="border rounded-lg bg-white overflow-hidden shadow-sm"
          >
            <div
              class="bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-600"
            >
              {{ ss.viewport }}
              <span
                class="ml-2 px-1.5 py-0.5 rounded text-xs font-bold"
                :class="
                  ss.similarity >= 95
                    ? 'bg-green-100 text-green-700'
                    : ss.similarity >= 80
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-red-100 text-red-700'
                "
              >
                {{ ss.similarity }}%
              </span>
            </div>
            <div class="p-2 grid grid-cols-3 gap-1">
              <!-- 3D File -->
              <div class="text-center">
                <p class="text-xxs text-gray-400 mb-1">3D File</p>
                <img
                  v-if="ss.threeDFileUrl"
                  :src="ss.threeDFileUrl"
                  class="w-full h-auto border rounded cursor-pointer hover:opacity-80 transition"
                  @click="openLightbox(ss.threeDFileUrl)"
                  alt="3D File screenshot"
                />
                <div
                  v-else
                  class="w-full h-20 bg-gray-200 flex items-center justify-center text-gray-400 text-xs"
                >
                  —
                </div>
              </div>
              <!-- Live -->
              <div class="text-center">
                <p class="text-xxs text-gray-400 mb-1">Live</p>
                <img
                  v-if="ss.liveUrl"
                  :src="ss.liveUrl"
                  class="w-full h-auto border rounded cursor-pointer hover:opacity-80 transition"
                  @click="openLightbox(ss.liveUrl)"
                  alt="Live screenshot"
                />
                <div
                  v-else
                  class="w-full h-20 bg-gray-200 flex items-center justify-center text-gray-400 text-xs"
                >
                  —
                </div>
              </div>
              <!-- Diff -->
              <div class="text-center">
                <p class="text-xxs text-red-400 mb-1">Diff</p>
                <img
                  v-if="ss.diffUrl"
                  :src="ss.diffUrl"
                  class="w-full h-auto border rounded cursor-pointer hover:opacity-80 transition"
                  @click="openLightbox(ss.diffUrl)"
                  alt="Diff image"
                />
                <div
                  v-else
                  class="w-full h-20 bg-gray-200 flex items-center justify-center text-gray-400 text-xs"
                >
                  —
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- DOM Comparison -->
      <div class="border rounded-lg bg-white p-4">
        <h3 class="text-base font-bold mb-2 flex items-center gap-2">
          <Layers class="w-4 h-4 text-indigo-600" />
          <span>DOM Structure Comparison</span>
          <span
            class="ml-2 text-xs px-2 py-0.5 rounded"
            :class="
              report.domComparison?.matches
                ? 'bg-green-100 text-green-700'
                : 'bg-yellow-100 text-yellow-700'
            "
          >
            {{ report.domComparison?.matches ? "Match" : "Differences found" }}
          </span>
        </h3>
        <p class="text-sm text-gray-600 mb-2">
          {{ report.domComparison?.summary }}
        </p>
        <div v-if="report.domComparison?.differences?.length" class="space-y-1">
          <div
            v-for="(diff, i) in report.domComparison.differences"
            :key="'dom-' + i"
            class="flex items-center gap-2 text-xs border-b py-1"
          >
            <code
              class="bg-gray-100 px-1.5 py-0.5 rounded text-gray-700 font-mono"
              >{{ diff.element }}</code
            >
            <span
              class="px-1.5 py-0.5 rounded text-white text-xxs font-semibold"
              :class="diff.type === 'added' ? 'bg-green-500' : 'bg-red-500'"
            >
              {{ diff.type === "added" ? "ADDED" : "REMOVED" }}
            </span>
            <span class="text-gray-600">{{ diff.message }}</span>
            <span class="text-gray-400 ml-auto">
              3D: {{ diff.threeDFileCount }} → Live: {{ diff.liveCount }}
            </span>
          </div>
        </div>
        <div v-else class="text-sm text-green-600 flex items-center gap-1.5">
          <CheckCircle2 class="w-4 h-4 text-green-500" />
          <span>DOM structures match.</span>
        </div>
      </div>

      <!-- CSS Comparison -->
      <div class="border rounded-lg bg-white p-4">
        <h3 class="text-base font-bold mb-2 flex items-center gap-2">
          <Palette class="w-4 h-4 text-pink-600" />
          <span>CSS Style Comparison</span>
          <span
            class="ml-2 text-xs px-2 py-0.5 rounded"
            :class="
              report.cssComparison?.matches
                ? 'bg-green-100 text-green-700'
                : 'bg-yellow-100 text-yellow-700'
            "
          >
            {{ report.cssComparison?.matches ? "Match" : "Differences found" }}
          </span>
        </h3>
        <p class="text-sm text-gray-600 mb-2">
          {{ report.cssComparison?.summary }}
        </p>
        <div v-if="report.cssComparison?.differences?.length" class="space-y-1">
          <div
            v-for="(diff, i) in report.cssComparison.differences"
            :key="'css-' + i"
            class="text-xs border-b py-1"
          >
            <code class="bg-gray-100 px-1 py-0.5 rounded font-mono">{{
              diff.element
            }}</code>
            <span class="text-gray-500"> · {{ diff.property }}:</span>
            <span class="text-red-500 line-through ml-1">{{
              diff.threeDFile
            }}</span>
            <span class="mx-1">→</span>
            <span class="text-green-600">{{ diff.live }}</span>
          </div>
        </div>
        <div v-else class="text-sm text-green-600 flex items-center gap-1.5">
          <CheckCircle2 class="w-4 h-4 text-green-500" />
          <span>Key CSS styles match.</span>
        </div>
      </div>

      <!-- Section-by-Section Layout Comparison -->
      <div
        v-if="report.sectionBySection && report.sectionBySection.length"
        class="border rounded-lg bg-white p-4"
      >
        <h3 class="text-base font-bold mb-3 flex items-center gap-2">
          <LayoutGrid class="w-4 h-4 text-blue-600" />
          <span>Section-by-Section Layout Comparison</span>
        </h3>
        <div class="space-y-2">
          <div
            v-for="(sec, i) in report.sectionBySection"
            :key="'sec-' + i"
            class="flex items-start gap-3 border-b last:border-b-0 py-2"
          >
            <span class="text-lg flex-shrink-0">{{ sec.icon }}</span>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <span
                  class="text-sm font-semibold"
                  :class="
                    sec.status === 'identical'
                      ? 'text-green-700'
                      : 'text-yellow-700'
                  "
                >
                  {{ sec.section }}
                </span>
                <span
                  class="text-xxs px-1.5 py-0.5 rounded font-bold uppercase"
                  :class="
                    sec.status === 'identical'
                      ? 'bg-green-100 text-green-700'
                      : sec.status === 'missing-in-3d' ||
                        sec.status === 'missing-in-live'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-yellow-100 text-yellow-700'
                  "
                >
                  {{
                    sec.status === "identical"
                      ? "Match"
                      : sec.status === "missing-in-3d" ||
                        sec.status === "missing-in-live"
                      ? "Missing"
                      : "Differs"
                  }}
                </span>
              </div>
              <p
                class="text-xs mt-0.5"
                :class="
                  sec.status === 'identical'
                    ? 'text-green-600'
                    : 'text-yellow-600'
                "
              >
                {{ sec.message }}
              </p>
              <!-- Expandable details for diffs -->
              <div v-if="sec.details && sec.details.differences" class="mt-1">
                <button
                  class="text-xxs text-blue-500 hover:text-blue-700 underline"
                  @click="toggleSectionDetail(i)"
                >
                  {{ expandedSections[i] ? "Hide" : "Show" }} details
                </button>
                <div
                  v-if="expandedSections[i]"
                  class="mt-1 text-xxs space-y-0.5"
                >
                  <div
                    v-for="(d, j) in sec.details.differences"
                    :key="'det-' + j"
                    class="text-gray-500"
                  >
                    ⦁ {{ d }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Console Errors -->
      <div v-if="hasConsoleErrors" class="border rounded-lg bg-white p-4">
        <h3 class="text-base font-bold mb-2 flex items-center gap-2">
          <Bug class="w-4 h-4 text-red-500" />
          <span>Console / JavaScript Errors</span>
          <span
            class="ml-2 text-xs px-2 py-0.5 rounded bg-red-100 text-red-700"
          >
            {{ totalConsoleErrors }} error(s)
          </span>
        </h3>
        <div v-if="report.consoleErrors?.threeDFile?.length" class="mb-3">
          <p class="text-xs font-semibold text-gray-500 mb-1">3D File Link:</p>
          <div class="space-y-1 max-h-32 overflow-y-auto">
            <div
              v-for="(err, i) in report.consoleErrors.threeDFile"
              :key="'ce3d-' + i"
              class="text-xs bg-red-50 text-red-700 px-2 py-1 rounded font-mono"
            >
              {{ err }}
            </div>
          </div>
        </div>
        <div v-if="report.consoleErrors?.live?.length">
          <p class="text-xs font-semibold text-gray-500 mb-1">Live Link:</p>
          <div class="space-y-1 max-h-32 overflow-y-auto">
            <div
              v-for="(err, i) in report.consoleErrors.live"
              :key="'celive-' + i"
              class="text-xs bg-red-50 text-red-700 px-2 py-1 rounded font-mono"
            >
              {{ err }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── Empty state ───────────────────────────────────────────────────── -->
    <div v-else-if="!comparing" class="text-center py-16 text-gray-400">
      <div class="mb-4 flex justify-center">
        <Crosshair class="w-12 h-12 text-gray-400" />
      </div>
      <p class="text-lg font-medium">Ready to compare</p>
      <p class="text-sm mt-1">
        Enter a 3D file URL and a live site URL above, then click "Compare
        Designs".
      </p>
    </div>

    <!-- ── Lightbox ──────────────────────────────────────────────────────── -->
    <div
      v-if="lightboxUrl"
      class="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center cursor-pointer"
      @click="lightboxUrl = null"
    >
      <img
        :src="lightboxUrl"
        class="max-w-[90vw] max-h-[90vh] rounded shadow-2xl"
      />
      <button
        class="absolute top-4 right-4 text-white hover:text-gray-300 p-2"
        @click="lightboxUrl = null"
        aria-label="Close lightbox"
      >
        <X class="w-6 h-6" />
      </button>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import baseUrl from "../config";
import {
  Search,
  CheckCircle2,
  XCircle,
  FileText,
  Home,
  ChevronUp,
  ChevronDown,
  Camera,
  Layers,
  Palette,
  LayoutGrid,
  Bug,
  Crosshair,
  X,
} from "lucide-vue-next";

export default {
  name: "DesignComparison",
  components: {
    Search,
    CheckCircle2,
    XCircle,
    FileText,
    Home,
    ChevronUp,
    ChevronDown,
    Camera,
    Layers,
    Palette,
    LayoutGrid,
    Bug,
    Crosshair,
    X,
  },
  props: {
    cachedData: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      threeDFileUrl: "",
      liveUrl: "",
      comparing: false,
      error: "",
      report: null,
      lightboxUrl: null,
      expandedSections: {},
      expandedPages: {},
    };
  },
  computed: {
    hasConsoleErrors() {
      return (
        (this.report?.consoleErrors?.threeDFile?.length || 0) +
          (this.report?.consoleErrors?.live?.length || 0) >
        0
      );
    },
    totalConsoleErrors() {
      return (
        (this.report?.consoleErrors?.threeDFile?.length || 0) +
        (this.report?.consoleErrors?.live?.length || 0)
      );
    },
  },
  watch: {
    cachedData: {
      immediate: true,
      handler(val) {
        if (val && !this.report) {
          this.threeDFileUrl = val.three_df_file_url || "";
          this.liveUrl = val.live_url || "";
          this.report = {
            status: val.status,
            similarityScore: val.similarity_score,
            summary: val.summary,
            screenshots: val.screenshots || [],
            pages: val.pages || [],
            pagesCompared: val.pages_compared || 0,
            domComparison: val.dom_comparison || [],
            cssComparison: val.css_comparison || [],
            sectionBySection: val.section_by_section || [],
            consoleErrors: val.console_errors || { threeDFile: [], live: [] },
            timestamp: val.scanned_at || "",
          };
        }
      },
    },
  },
  methods: {
    async runComparison() {
      this.error = "";
      this.report = null;
      this.comparing = true;

      const token = localStorage.getItem("token");
      if (!token) {
        this.error = "You are not authenticated. Please log in.";
        this.comparing = false;
        return;
      }

      try {
        const response = await axios.post(
          `${baseUrl}/design-compare`,
          {
            threeDFileUrl: this.threeDFileUrl,
            liveUrl: this.liveUrl,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 300000, // 5 minutes — matches PHP set_time_limit
          }
        );
        this.report = response.data;
      } catch (err) {
        this.error =
          err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Comparison failed. Please try again.";
      } finally {
        this.comparing = false;
      }
    },
    resetForm() {
      this.report = null;
      this.error = "";
      this.threeDFileUrl = "";
      this.liveUrl = "";
      this.expandedSections = {};
      this.expandedPages = {};
    },
    openLightbox(url) {
      this.lightboxUrl = url;
    },
    toggleSectionDetail(index) {
      this.expandedSections[index] = !this.expandedSections[index];
    },
    togglePageDetail(index) {
      this.expandedPages[index] = !this.expandedPages[index];
    },
  },
};
</script>

<style scoped>
.design-comparison {
  max-width: 1280px;
  margin: 0 auto;
}
</style>
